import {
  FormField,
  RadioBox,
  RadioGroup,
  TextInput,
} from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { NotificationType, useNotifications } from 'modules/app/notifications';
import { useCostCentersQuery } from 'modules/budgets/apis';
import { MultipleCostCenterAutocomplete } from 'modules/company/cost-centers/components/MultipleCostCenterAutocomplete';
import { useCreateEcQuery } from 'modules/company/expense-categories/containers/EcFormModalContainer/hooks/useCreateEcQuery';
import { TaskStepLayout } from 'modules/onboarding/components/TaskStepLayout';
import { useUpdateCachedTaskStatus } from 'modules/onboarding/setup-hub/pages/SetupHubPage/hooks/useUpdateCachedTaskStatus';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { routeFor } from 'src/core/constants/routes';
import { useExpenseCategoriesQuery } from 'src/core/modules/company/expense-categories/containers/EcContainer/hooks/useExpenseCategoriesQuery';

import { validate } from './form';
import { type FormValues } from './types';
import { routes } from '../../routes';
import { getHelpVideoUrl } from '../utils';

export const ControlSettingsNewExpenseCategory = () => {
  const { t, activeLanguage } = useTranslation('global');
  const history = useHistory();
  const { pushNotif } = useNotifications();

  const companyId = useCompanyId();

  const updateCachedTaskStatus = useUpdateCachedTaskStatus();
  const costCentersQueryState = useCostCentersQuery();
  const costCenters = unwrapQuery(costCentersQueryState) || [];
  const [createExpenseCategory] = useCreateEcQuery();

  const expenseCategoriesQueryState = useExpenseCategoriesQuery();
  const expenseCategories = unwrapQuery(expenseCategoriesQueryState) || [];

  const [hasSubmittedOnce, setHasSubmittedOnce] = useState<boolean>(false);

  const goBackToHub = () => {
    const hubPath = routeFor(routes.SETUP_HUB_TASK_LIST.path, {
      company: companyId,
    });
    history.push(hubPath);
  };

  const { errors, values, handleChange, handleSubmit, setFieldValue } =
    useFormik<FormValues>({
      initialValues: {
        name: '',
        costCenterIds: [],
        assignToAllCostCenters: true,
      },
      validate: (valuesToValidate) =>
        validate({
          values: valuesToValidate,
          usedNames: expenseCategories.map(({ name }) => name),
          t,
        }),
      validateOnChange: hasSubmittedOnce,
      onSubmit: async (expenseCategoryValues) => {
        try {
          const payload = {
            name: expenseCategoryValues.name.trim(),
            costCenterIds: expenseCategoryValues.assignToAllCostCenters
              ? []
              : expenseCategoryValues.costCenterIds,
          };
          await createExpenseCategory(payload);

          updateCachedTaskStatus('expense_categories', 'setup_hub');
          const nextPagePath = routeFor(
            routes.CONTROL_SETTINGS_EXPENSE_CATEGORY_LIST.path,
            {
              company: companyId,
            },
          );
          history.push(nextPagePath);
        } catch {
          pushNotif({
            type: NotificationType.Danger,
            message: t('misc.errors.unknownError'),
          });
        }
      },
    });

  const onContinue = async () => {
    if (!hasSubmittedOnce) {
      setHasSubmittedOnce(true);
    }
    handleSubmit();
  };

  return (
    <TaskStepLayout
      onBack={goBackToHub}
      onNext={onContinue}
      nextButtonText={t('setupHub.newExpenseCategory.onNext')}
      video={{
        url: getHelpVideoUrl('expense_categories', activeLanguage),
        title: t('misc.needHelp'),
      }}
      description={t('setupHub.newExpenseCategory.callout.description')}
      title={t('setupHub.newExpenseCategory.title')}
    >
      <form onSubmit={handleSubmit}>
        <FormField
          className="mb-24"
          label={t('expenseCategories.form.name')}
          alertMessage={errors.name}
        >
          <TextInput
            name="name"
            onChange={handleChange}
            placeholder={t('setupHub.newExpenseCategory.namePlaceholder')}
            value={values.name}
          />
        </FormField>
        <FormField
          label={t('expenseCategories.form.costCenterAssignationFieldName')}
          description={t(
            'expenseCategories.form.costCenterAssignationFieldDescription',
          )}
        >
          <RadioGroup
            name="assignToAllCostCenters"
            value={values.assignToAllCostCenters ? 'true' : 'false'}
            onChange={(event) =>
              setFieldValue(
                'assignToAllCostCenters',
                event.target.value === 'true',
              )
            }
          >
            <RadioBox
              value="true"
              label={t('expenseCategories.form.costCenterAssignation.all')}
            />
            <RadioBox
              value="false"
              label={t('expenseCategories.form.costCenterAssignation.some')}
            />
          </RadioGroup>
        </FormField>
        {!values.assignToAllCostCenters && (
          <FormField
            className="mt-8"
            label={t('misc.costCenter')}
            visuallyHideLabel
            alertMessage={errors.costCenterIds as string | undefined}
          >
            <MultipleCostCenterAutocomplete
              costCenters={costCenters}
              selectedCostCenterIds={values.costCenterIds}
              onSelect={(options) => {
                setFieldValue(
                  'costCenterIds',
                  options.map((option) => option.key),
                );
              }}
            />
          </FormField>
        )}
      </form>
    </TaskStepLayout>
  );
};
